import {create} from 'zustand'
import {devtools} from 'zustand/middleware'
import Order, {Customer, Installation, SteddiPrices} from "../types/Order";
import {AccountInfo} from "../types/AccountInfo";
import {PaymentFreeMonthData} from "../types/PaymentFreeMonthData";
import PriceList from "../types/PriceList";

/**
 * This is the interface for the methods that will be available to the store.
 */
interface OrderStateMethods {
  setCustomer: (newCustomer: Customer) => void
  setDocumentId: (documentId: number) => void
  setOrder: (order: Order) => void
  setFirstPaymentDue: (date: Date) => void
  setPaymentFreeMonths: (freeMonths: number[]) => void
  setPriceEstimate: (price: number) => void
  setOrderHash: (hash: string) => void
  setEstimateLoading: (loading: boolean) => void
  setAccount: (account: AccountInfo) => void
  setPaymentFreeMonthData: (account: PaymentFreeMonthData) => void
}

/**
 * This is the interface for the state that will be available to the store.
 */
export interface OrderState extends OrderStateMethods {
  customer: Customer | null,
  esignDocumentId: number | null,
  paymentFreeMonths: number[],
  startUpDate: Date | undefined,
  brandId: number | null,
  firstPaymentDue: Date | null
  installations: Installation[]
  priceEstimate: number | null
  priceEstimates: PriceList | null
  orderHash: string | null
  estimateLoading: boolean
  totalEstimatedConsumption: number | null
  account: AccountInfo | null
  paymentFree: PaymentFreeMonthData | null
  steddiPrices: SteddiPrices | null
}

/**
 * This is the actual store.
 */
const useOrderState = create<OrderState>()(devtools(
  (set, get) => ({
    customer: null,
    esignDocumentId: null,
    paymentFreeMonths: [],
    startUpDate: undefined,
    brandId: null,
    firstPaymentDue: null,
    installations: [],
    priceEstimate: null,
    priceEstimates: null,
    estimateLoading: false,
    orderHash: null,
    totalEstimatedConsumption: null,
    account: null,
    paymentFree: null,
    steddiPrices: null,
    setCustomer: (newCustomer) => {
      newCustomer.invoicePostNumber ??= ""
      set({customer: newCustomer})
    },
    setDocumentId: (documentId) => set({esignDocumentId: documentId}),
    setOrder: (order) => {
      order.customer.invoicePostArea ??= ""
      set({
        esignDocumentId: order.ESignDocumentId,
        customer: order.customer,
        startUpDate: order.startUpDate,
        brandId: order.brandId,
        paymentFreeMonths: order.paymentFreeMonths,
        installations: order.installations,
        priceEstimate: order.estimatedMonthlyPrice,
        totalEstimatedConsumption: order.totalEstimatedConsumption,
        priceEstimates: order.estimatedMonthlyPrices,
        steddiPrices: order.steddiPrices
      })
    },
    setPaymentFreeMonths: (freeMonths) => set({paymentFreeMonths: freeMonths}),
    setFirstPaymentDue: (date) => set({firstPaymentDue: date}),
    setPriceEstimate: (price) => set({priceEstimate: price}),
    setOrderHash: (hash) => set({orderHash: hash}),
    setEstimateLoading: (loading) => set({estimateLoading: loading}),
    setAccount: (account) => set({account: account, installations: account.installations}),
    setPaymentFreeMonthData: (paymentFree) => {
      let estimate = 0
      switch(paymentFree.paymentFreeMonths.length){
        case 0:
          estimate = paymentFree.estimates.none
          break
        case 1:
          estimate = paymentFree.estimates.single
          break
        case 2:
          estimate = paymentFree.estimates.double
          break
      }
      return set({paymentFree: paymentFree, installations: paymentFree.installations, paymentFreeMonths: paymentFree.paymentFreeMonths, priceEstimate: estimate, steddiPrices: paymentFree.steddiPrices})
    }
  })
))

export default useOrderState